// src/admin/useDialogEscape.ts
import { useEffect, useRef, useState } from 'react';

/**
 * Dialog dismissal the Modal primitive leaves to callers: Escape closes unless a submit is in
 * flight, and focus returns to the element that opened the dialog when it unmounts. The opener is
 * captured during the first render, before autoFocus moves focus into the dialog.
 */
export function useDialogEscape(onClose: () => void, enabled: boolean): void {
  const [opener] = useState<HTMLElement | null>(() =>
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  // Deferred a frame so StrictMode's simulated unmount, immediately followed by a remount, cancels
  // the restore instead of pulling focus out of the dialog it just opened.
  const focusFrameRef = useRef<number | null>(null);
  useEffect(() => {
    if (focusFrameRef.current !== null) {
      cancelAnimationFrame(focusFrameRef.current);
      focusFrameRef.current = null;
    }
    return () => {
      focusFrameRef.current = requestAnimationFrame(() => {
        focusFrameRef.current = null;
        opener?.focus();
      });
    };
  }, [opener]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, enabled]);
}
